// useUndoStack — Undo 스택 훅 (U53).
//
// 스택 크기: 최대 1건 (LRU — 새 push 시 이전 항목 덮어씀).
// push(entry): 새 undo 항목 등록. duration(기본 5000ms) 후 자동 제거.
// undoLast(): 스택 최상단 항목의 undo() 호출 후 스택 비움.
// clear(): 타이머 포함 즉시 스택 비움.
// entry: { description, undo, duration? }

import { useCallback, useEffect, useRef, useState } from 'react';

// ── 타입 ────────────────────────────────────────────────────

/** Undo 항목 */
export interface UndoEntry {
  /** 사용자에게 표시할 설명 (예: "비밀번호 삭제됨") */
  description: string;
  /** 실제 복원 비동기 함수 */
  undo: () => Promise<void>;
  /** 자동 제거까지 남은 ms (기본 5000) */
  duration?: number;
}

/** 스택 내부 항목 (id + 만료 시각 포함) */
interface StackEntry extends UndoEntry {
  id: number;
  /** 토스트 생성 시각 (Date.now()) */
  createdAt: number;
  /** 만료 시각 (createdAt + duration) */
  expiresAt: number;
}

/** useUndoStack 반환 타입 */
export interface UndoStackValue {
  /** 현재 활성 undo 항목 (없으면 null) */
  current: StackEntry | null;
  /** 새 undo 항목 등록 */
  push: (entry: UndoEntry) => void;
  /** 현재 항목 undo 실행 후 스택 비움 */
  undoLast: () => Promise<void>;
  /** 스택 즉시 비움 (타이머 취소 포함) */
  clear: () => void;
}

// ── 전역 ID 카운터 ─────────────────────────────────────────
let _nextId = 0;

// ── useUndoStack hook ──────────────────────────────────────

/**
 * Undo 스택 훅.
 * 최대 1건 유지. 새 push 시 이전 항목은 타이머를 취소하고 즉시 교체.
 */
export function useUndoStack(): UndoStackValue {
  const [current, setCurrent] = useState<StackEntry | null>(null);

  // 자동 제거 타이머 ref — 리렌더 없이 취소 가능
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // 타이머 정리 헬퍼
  const clearTimer = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  // 언마운트 시 타이머 정리
  useEffect(() => {
    return () => {
      clearTimer();
    };
  }, [clearTimer]);

  const clear = useCallback(() => {
    clearTimer();
    setCurrent(null);
  }, [clearTimer]);

  const push = useCallback(
    (entry: UndoEntry) => {
      // 이전 타이머 취소 (기존 항목 즉시 교체)
      clearTimer();

      const duration = entry.duration ?? 5000;
      const createdAt = Date.now();
      const id = ++_nextId;
      const stackEntry: StackEntry = {
        ...entry,
        id,
        createdAt,
        expiresAt: createdAt + duration,
        duration,
      };

      setCurrent(stackEntry);

      // duration 후 자동 제거
      timerRef.current = setTimeout(() => {
        setCurrent((prev) => (prev?.id === id ? null : prev));
        timerRef.current = null;
      }, duration);
    },
    [clearTimer],
  );

  const undoLast = useCallback(async () => {
    if (!current) return;
    // 스택을 즉시 비워 토스트 닫기 (undo 실행 전에 UI 갱신)
    clear();
    await current.undo();
  }, [current, clear]);

  return { current, push, undoLast, clear };
}
